import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_PROPERTY, GET_PROPERTY_DETAILS, GET_USERS, ARCHIVE_PROPERTY, DELETE_PROPERTY, PROPERTY_HAS_RELATIONS, GET_CANCELLED_JOBS_COUNT, GET_FUTURE_JOBS_COUNT, DELETE_FUTURE_WALKTHROUGH_JOBS } from '../graphql/queries';
import { GET_QUICKBOOKS_CONNECTION_STATUS, GET_QUICKBOOKS_CUSTOMERS } from '../graphql/quickbooks';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { navigateBackToProperties } from './Properties';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import HvacFiltersSection, { formatHvacLastChangedDate } from '../components/properties/HvacFiltersSection';
import PropertyQuickLinks from '../components/properties/PropertyQuickLinks';
import GuestInstructionalVideosSection from '../components/properties/GuestInstructionalVideosSection';
import AdditionalInfo from '../components/properties/AdditionalInfo';
import TeamAssignment from '../components/properties/TeamAssignment';
import { buildPropertyManagementInput, getPropertyManagementFormValues } from '../components/properties/PropertyManagementAssignment';

// Property Area options
const PROPERTY_AREAS = [
  { value: 'KIAWAH', label: 'Kiawah Island' },
  { value: 'ISLE_OF_PALMS', label: 'Isle of Palms' },
  { value: 'ISLE_OF_PALMS_WILD_DUNES', label: 'Isle of Palms: Wild Dunes' },
  { value: 'SEABROOK_ISLAND', label: 'Seabrook Island' }
];

// Base input styles - replace all input field classes with this improved version
const baseInputStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors";

// Money input styles with padding for $ symbol
const moneyInputStyles = "pl-8 pr-4 py-2.5 h-11 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors";

// Textarea styles
const textareaStyles = "px-4 py-3 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors resize-none";

// Select styles
const selectStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors appearance-none bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em] bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M10%203a1%201%200%2001.707.293l3%203a1%201%200%2001-1.414%201.414L10%205.414%207.707%207.707a1%201%200%2001-1.414-1.414l3-3A1%201%200%200110%203zm-3.707%209.293a1%201%200%20011.414%200L10%2014.586l2.293-2.293a1%201%200%20011.414%201.414l-3%203a1%201%200%2001-1.414%200l-3-3a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] pr-10";

export default function EditProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get the pagination state that was passed when navigating to edit
  const returnToPage = location.state?.returnToPage || 1;
  const returnToSearch = location.state?.returnToSearch || '';
  const returnToAreas = location.state?.returnToAreas || [];

  // Add query to fetch users
  const { data: usersData } = useQuery(GET_USERS);

  // Get all users for use in dropdowns
  const allUsers = usersData?.users || [];

  // Add query to fetch property data
  const { data: propertyData, loading: propertyLoading } = useQuery(GET_PROPERTY_DETAILS, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });
  const hydratedPropertyIdRef = useRef(null);

  // Query to check if property has relations
  const { data: relationsData } = useQuery(PROPERTY_HAS_RELATIONS, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only'
  });

  // QuickBooks queries
  const { data: quickBooksConnectionData } = useQuery(GET_QUICKBOOKS_CONNECTION_STATUS, {
    fetchPolicy: 'network-only',
    skip: user?.role !== 'ADMIN' && user?.role !== 'PROPERTY_MANAGER'
  });

  const { data: quickBooksCustomersData, loading: quickBooksCustomersLoading } = useQuery(GET_QUICKBOOKS_CUSTOMERS, {
    fetchPolicy: 'network-only',
    skip: !quickBooksConnectionData?.quickBooksConnectionStatus?.isConnected || (user?.role !== 'ADMIN' && user?.role !== 'PROPERTY_MANAGER')
  });

  const quickBooksCustomers = quickBooksCustomersData?.quickBooksCustomers || [];
  const isQuickBooksConnected = quickBooksConnectionData?.quickBooksConnectionStatus?.isConnected || false;

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    area: '',
    checkInTime: '',
    checkOutTime: '',
    entryDetails: '',
    propertyManagementTeamId: '',
    propertyManagerId: '',
    beds: '',
    baths: '',
    housekeepingNotes: '',
    stagingNotes: '',
    propertyManagerNotes: '',
    websiteLink: '',
    price: '',
    ownerPrice: '',
    payout: '',
    externalId: '',
    integratedWithStreamline: false,
    cleaningAssignedUserId: '',
    inspectionAssignedUserId: '',
    walkthroughAssignedUserId: '',
    walkthroughJobCreationEnabled: true,
    stagingPhotos: [],
    quickbooksCustomerId: '',
    hvacFilters: [],
    hvacFiltersLastChanged: null,
    instructionalVideos: []
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteAction, setDeleteAction] = useState(''); // 'archive' or 'delete'
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteWalkthroughDialog, setShowDeleteWalkthroughDialog] = useState(false);

  const [updateProperty, { loading: updateLoading, error: updateError }] = useMutation(UPDATE_PROPERTY);
  const [archiveProperty, { loading: archiveLoading }] = useMutation(ARCHIVE_PROPERTY);
  const [deleteProperty, { loading: deleteLoading }] = useMutation(DELETE_PROPERTY);
  const [deleteFutureWalkthroughJobs, { loading: deleteWalkthroughLoading }] = useMutation(DELETE_FUTURE_WALKTHROUGH_JOBS);

  // Query to get future jobs count for archive confirmation
  const { data: futureJobsData, loading: futureJobsLoading } = useQuery(GET_FUTURE_JOBS_COUNT, {
    variables: { 
      propertyId: id,
      archiveDate: new Date().toISOString().split('T')[0] // Today's date
    },
    skip: !id || !showArchiveDialog,
    fetchPolicy: 'network-only'
  });

  const handleBackToProperties = () => {
    navigateBackToProperties(navigate, returnToPage, returnToSearch, returnToAreas);
  };

  useEffect(() => {
    const property = propertyData?.property;
    if (!property?.id) return;
    if (hydratedPropertyIdRef.current === property.id) return;
    hydratedPropertyIdRef.current = property.id;
    setFormData({
      name: property.name || '',
      address: property.address || '',
      city: property.city || '',
      state: property.state || '',
      zip: property.zip || '',
      area: property.area || '',
      checkInTime: property.checkInTime || '',
      checkOutTime: property.checkOutTime || '',
      entryDetails: property.entryDetails || '',
      ...getPropertyManagementFormValues(property),
      beds: property.beds || '',
      baths: property.baths || '',
      housekeepingNotes: property.housekeepingNotes || '',
      stagingNotes: property.stagingNotes || '',
      propertyManagerNotes: property.propertyManagerNotes || '',
      websiteLink: property.websiteLink || '',
      price: property.price ? Number(property.price).toFixed(2) : '',
      ownerPrice: property.ownerPrice ? Number(property.ownerPrice).toFixed(2) : '',
      payout: property.payout ? Number(property.payout).toFixed(2) : '',
      externalId: property.externalId || '',
      integratedWithStreamline: property.integratedWithStreamline || false,
      cleaningAssignedUserId: property.cleaningAssignedUserId || '',
      inspectionAssignedUserId: property.inspectionAssignedUserId || '',
      walkthroughAssignedUserId: property.walkthroughAssignedUserId || '',
      walkthroughJobCreationEnabled: property.walkthroughJobCreationEnabled !== undefined ? property.walkthroughJobCreationEnabled : true,
      stagingPhotos: property.stagingPhotos || [],
      quickbooksCustomerId: property.quickbooksCustomerId || '',
      hvacFilters: property.hvacFilters || [],
      hvacFiltersLastChanged: formatHvacLastChangedDate(property.hvacFiltersLastChanged),
      instructionalVideos: (property.instructionalVideos || [])
        .slice()
        .sort((a, b) => {
          const orderA = typeof a.sortOrder === 'number' ? a.sortOrder : 0;
          const orderB = typeof b.sortOrder === 'number' ? b.sortOrder : 0;
          return orderA - orderB;
        })
        .map((video, index) => ({
          id: video.id || `temp-${index}`,
          title: video.title || '',
          description: video.description || '',
          videoUrl: video.videoUrl || ''
        }))
    });
  }, [propertyData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberInputScroll = (e) => {
    // Prevent scroll from changing the input value
    e.target.blur();
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let processedFile = file;

      // Handle HEIC/HEIF conversion if needed
      if (file.type === 'image/heic' || file.type === 'image/heif' || 
          file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        toast.loading("Converting iPhone HEIC image...", { id: "heic-conversion" });
        
        try {
          const heic2any = (await import('heic2any')).default;
          const jpegBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8
          });
          
          processedFile = new File(
            [jpegBlob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            { type: 'image/jpeg' }
          );
          
          toast.success("HEIC image converted successfully", { id: "heic-conversion" });
        } catch (conversionError) {
          console.error("Error converting HEIC image:", conversionError);
          toast.dismiss("heic-conversion");
          toast.error("Unable to convert HEIC image. Please convert it to JPEG or PNG before uploading.");
          setIsUploading(false);
          return;
        }
      }

      // Validate file size (10MB max)
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      if (processedFile.size > MAX_FILE_SIZE) {
        toast.error("Image is too large. Maximum size is 10MB.");
        setIsUploading(false);
        return;
      }

      // Create a preview
      try {
        const previewUrl = URL.createObjectURL(processedFile);
        setImagePreview(previewUrl);
        setUploadProgress(10);
      } catch (previewError) {
        console.error("Error creating preview:", previewError);
        toast.error("Failed to generate image preview");
      }

      try {
        // Upload to S3 using proper endpoint
        const response = await fetch(`${API_URL}/api/properties/get-upload-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            fileName: processedFile.name,
            fileType: processedFile.type
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to get upload URL: ${response.statusText}`);
        }

        const { uploadUrl, imageUrl, headers } = await response.json();
        setUploadProgress(50);

        // Upload file to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: headers,
          body: processedFile
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
        }

        setUploadProgress(80);
        
        // Add the S3 URL to the array
        const updatedPhotos = [...formData.stagingPhotos, imageUrl];
        setFormData(prev => ({
          ...prev,
          stagingPhotos: updatedPhotos
        }));
        
        setUploadProgress(100);
        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image. Please try again.');
        setUploadProgress(0);
        setImagePreview(null);
        setIsUploading(false);
      }
    } catch (outerError) {
      console.error('Error in file upload process:', outerError);
      toast.error('Failed to process file. Please try again.');
      setIsUploading(false);
      setImagePreview(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleRemovePhoto = (indexToRemove) => {
    const updatedPhotos = formData.stagingPhotos.filter((_, index) => index !== indexToRemove);
    setFormData(prev => ({
      ...prev,
      stagingPhotos: updatedPhotos
    }));
  };

  const handleArchiveProperty = () => {
    setShowArchiveDialog(true);
  };

  const handleConfirmArchive = async () => {
    try {
      console.log('Attempting to archive property:', { id, userRole: user?.role });
      const result = await archiveProperty({
        variables: { id }
      });
      console.log('Archive result:', result);
      toast.success('Property archived successfully');
      setShowArchiveDialog(false);
      // Navigate back to properties list
      handleBackToProperties();
    } catch (error) {
      console.error('Error archiving property:', error);
      console.error('Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      });
      toast.error('Failed to archive property: ' + error.message);
    }
  };

  const handleCancelArchive = () => {
    setShowArchiveDialog(false);
  };

  const handleDeleteProperty = () => {
    setDeleteAction('delete');
    setShowDeleteDialog(true);
  };

  const confirmAction = async () => {
    try {
      if (deleteAction === 'delete') {
        await deleteProperty({
          variables: { id }
        });
        toast.success('Property deleted successfully');
        setShowDeleteDialog(false);
        handleBackToProperties();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete property');
    }
  };

  const cancelAction = () => {
    setShowDeleteDialog(false);
    setDeleteAction('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Helper function to handle text fields
    const formatTextField = (value) => {
      if (value === null || value === undefined) return null;
      const trimmed = String(value).trim();
      return trimmed === '' ? null : trimmed;
    };

    // Helper function to handle numeric fields
    const parseNumericField = (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const parsed = Number(value);
      return isNaN(parsed) ? null : parsed;
    };

    const input = {
      name: formatTextField(formData.name),
      address: formatTextField(formData.address),
      city: formatTextField(formData.city),
      state: formatTextField(formData.state),
      zip: formatTextField(formData.zip),
      area: formatTextField(formData.area),
      checkInTime: formatTextField(formData.checkInTime),
      checkOutTime: formatTextField(formData.checkOutTime),
      entryDetails: formatTextField(formData.entryDetails),
      ...buildPropertyManagementInput(formData),
      beds: parseNumericField(formData.beds),
      baths: parseNumericField(formData.baths),
      housekeepingNotes: formatTextField(formData.housekeepingNotes),
      stagingNotes: formatTextField(formData.stagingNotes),
      propertyManagerNotes: formatTextField(formData.propertyManagerNotes),
      websiteLink: formatTextField(formData.websiteLink),
      price: parseNumericField(formData.price),
      ownerPrice: parseNumericField(formData.ownerPrice),
      payout: parseNumericField(formData.payout),
      externalId: formatTextField(formData.externalId),
      integratedWithStreamline: formData.integratedWithStreamline,
      cleaningAssignedUserId: formatTextField(formData.cleaningAssignedUserId),
      inspectionAssignedUserId: formatTextField(formData.inspectionAssignedUserId),
      walkthroughAssignedUserId: formatTextField(formData.walkthroughAssignedUserId),
      walkthroughJobCreationEnabled: formData.walkthroughJobCreationEnabled,
      stagingPhotos: formData.stagingPhotos,
      quickbooksCustomerId: formatTextField(formData.quickbooksCustomerId),
      hvacFilters: formData.hvacFilters?.map(filter => ({
        id: filter.id?.startsWith('temp-') ? undefined : filter.id,
        filterLocation: formatTextField(filter.filterLocation),
        filterSize: formatTextField(filter.filterSize),
      })).filter(filter => filter.filterLocation && filter.filterSize) || [],
      hvacFiltersLastChanged: formatHvacLastChangedDate(formData.hvacFiltersLastChanged),
    };

    try {
      const { data } = await updateProperty({
        variables: {
          id,
          input
        },
        refetchQueries: [
          {
            query: GET_PROPERTY_DETAILS,
            variables: { id }
          }
        ]
      });

      if (data?.updateProperty) {
        toast.success('Property changes saved successfully');
        handleBackToProperties();
      }
    } catch (err) {
      console.error('Error updating property:', err);
      toast.error(`Failed to save property changes: ${err.message}`);
    }
  };

  // Show loading state if data is still loading
  if (propertyLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  const eligiblePropertyManagers = allUsers
    .filter(user => {
      const isSelected = String(formData.propertyManagerId) === String(user.id);
      if (isSelected) return true;
      if (!['PROPERTY_MANAGER', 'ADMIN', 'GUEST_APP_MANAGER'].includes(user.role)) return false;
      if (user.isActive === false) return false;
      if (formData.area && user.areas && Array.isArray(user.areas) && user.areas.length > 0) {
        const propertyArea = formData.area.toUpperCase();
        const userAreas = user.areas.map(area => area.toUpperCase());
        return userAreas.includes(propertyArea);
      }
      return true;
    })
    .sort((a, b) => {
      const nameA = a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
      const nameB = b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email;
      return nameA.localeCompare(nameB);
    });

  return (
    <div className="container mx-auto p-2 md:p-4 max-w-7xl">
      {/* Back button */}
      <div className="mb-3 md:mb-6">
        <button
          onClick={handleBackToProperties}
          className="inline-flex items-center text-gray-300 md:text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft className="mr-1" />
          <span>Back to Properties</span>
        </button>
      </div>

      {/* Property title */}
      <div className="mb-4 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white md:text-gray-900">
          Edit Property: {formData.name}
        </h1>
      </div>

      {/* Error message */}
      {updateError && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-200">
          {updateError.message}
        </div>
      )}

      {/* Quick Links Navigation */}
      <PropertyQuickLinks
        sections={[
          { id: 'basic-info', label: 'Basic Info' },
          { id: 'check-in', label: 'Check-in' },
          { id: 'entry-details', label: 'Entry Details' },
          { id: 'property-details', label: 'Property Details' },
          { id: 'team-assignment', label: 'Team Assignment' },
          { id: 'notes', label: 'Notes' },
          { id: 'additional-info', label: 'Additional Info' },
          { id: 'quickbooks', label: 'QuickBooks' },
          { id: 'job-management', label: 'Job Management' },
          { id: 'hvac-filters', label: 'HVAC Filters' },
          { id: 'staging-photos', label: 'Staging Photos' },
          { id: 'instructional-videos', label: 'Guest Instructional Videos' },
        ]}
        isEditPage={true}
      />

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info Section */}
        <div id="basic-info" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Information</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Property Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Zipcode</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Area</label>
                <select
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className={selectStyles}
                >
                  <option value="">Select an area</option>
                  {PROPERTY_AREAS.map((area) => (
                    <option key={area.value} value={area.value}>
                      {area.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Check-in/out Times Section */}
        <div id="check-in" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Check-in Information</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Check-in Time</label>
                <input
                  type="text"
                  name="checkInTime"
                  value={formData.checkInTime}
                  onChange={handleInputChange}
                  disabled={formData.integratedWithStreamline}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Check-out Time</label>
                <input
                  type="text"
                  name="checkOutTime"
                  value={formData.checkOutTime}
                  onChange={handleInputChange}
                  disabled={formData.integratedWithStreamline}
                  className={baseInputStyles}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Entry Details Section */}
        <div id="entry-details" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Entry Details</h3>
          </div>
          <div className="p-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Access Instructions</label>
              <textarea
                name="entryDetails"
                value={formData.entryDetails}
                onChange={handleInputChange}
                disabled={formData.integratedWithStreamline}
                rows="4"
                className={textareaStyles}
              />
            </div>
          </div>
        </div>
        
        {/* Property Details Section */}
        <div id="property-details" className="relative z-20 bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="rounded-t-lg px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Property Details</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Beds</label>
                <input
                  type="number"
                  step="0.1"
                  name="beds"
                  value={formData.beds}
                  onChange={handleInputChange}
                  disabled={formData.integratedWithStreamline}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Baths</label>
                <input
                  type="number"
                  step="0.1"
                  name="baths"
                  value={formData.baths}
                  onChange={handleInputChange}
                  disabled={formData.integratedWithStreamline}
                  className={baseInputStyles}
                />
              </div>
            </div>
          </div>
        </div>

        <div id="team-assignment" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <TeamAssignment
            users={eligiblePropertyManagers}
            teamId={formData.propertyManagementTeamId}
            managerId={formData.propertyManagerId}
            selectClassName={selectStyles}
            onAssignmentChange={(assignment) => setFormData((prev) => ({ ...prev, ...assignment }))}
            cleaningAssignedUserId={formData.cleaningAssignedUserId}
            inspectionAssignedUserId={formData.inspectionAssignedUserId}
            walkthroughAssignedUserId={formData.walkthroughAssignedUserId}
            area={formData.area}
            allUsers={allUsers}
            onInputChange={handleInputChange}
          />
        </div>
        
        {/* Notes Section */}
        <div id="notes" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Notes</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Housekeeping Notes</label>
              <textarea
                name="housekeepingNotes"
                value={formData.housekeepingNotes}
                onChange={handleInputChange}
                rows="3"
                className={textareaStyles}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Staging Notes</label>
              <textarea
                name="stagingNotes"
                value={formData.stagingNotes}
                onChange={handleInputChange}
                rows="3"
                className={textareaStyles}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Property Manager Notes</label>
              <textarea
                name="propertyManagerNotes"
                value={formData.propertyManagerNotes}
                onChange={handleInputChange}
                rows="3"
                className={textareaStyles}
              />
            </div>
          </div>
        </div>
        
        {/* Additional Info Section */}
        <div id="additional-info" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <AdditionalInfo
            websiteLink={formData.websiteLink}
            externalId={formData.externalId}
            price={formData.price}
            ownerPrice={formData.ownerPrice}
            payout={formData.payout}
            integratedWithStreamline={formData.integratedWithStreamline}
            onInputChange={handleInputChange}
            onNumberInputScroll={handleNumberInputScroll}
          />
        </div>

        {/* QuickBooks Integration Section */}
        {(user?.role === 'ADMIN' || user?.role === 'PROPERTY_MANAGER') && (
          <div id="quickbooks" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
            <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">QuickBooks Integration</h3>
              <p className="mt-1 text-sm text-gray-500">
                Assign this property to a QuickBooks customer
              </p>
            </div>
            <div className="p-6 space-y-4">
              {!isQuickBooksConnected ? (
                <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    QuickBooks is not connected. Please connect your QuickBooks account in Settings to assign customers to properties.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">QuickBooks Customer</label>
                  <select
                    name="quickbooksCustomerId"
                    value={formData.quickbooksCustomerId}
                    onChange={handleInputChange}
                    className={selectStyles}
                    disabled={quickBooksCustomersLoading}
                  >
                    <option value="">No customer assigned</option>
                    {[...quickBooksCustomers]
                      .sort((a, b) => {
                        if (a.active !== b.active) {
                          return a.active ? -1 : 1;
                        }
                        return a.displayName.localeCompare(b.displayName);
                      })
                      .map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.displayName}
                          {!customer.active ? ' (inactive)' : ''}
                          {customer.companyName && ` (${customer.companyName})`}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Select a QuickBooks customer to associate with this property
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Job Management Section */}
        <div id="job-management" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Job Management</h3>
            <p className="mt-1 text-sm text-gray-500">
              Control walkthrough job creation and management for this property
            </p>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enable Walkthrough Job Creation
                </label>
                <p className="text-xs text-gray-500">
                  When enabled, walkthrough jobs will be automatically created when reservations are synced or updated.
                  When disabled, no new walkthrough jobs will be created for this property.
                </p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="walkthroughJobCreationEnabled"
                    checked={formData.walkthroughJobCreationEnabled}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Delete Future Walkthrough Jobs</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Permanently cancel all future walkthrough jobs for this property. This action cannot be undone.
                    Only jobs with PENDING or READY_FOR_INSPECTION status will be cancelled.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteWalkthroughDialog(true)}
                  disabled={deleteWalkthroughLoading}
                  className="ml-4 px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteWalkthroughLoading ? 'Deleting...' : 'Delete Future Jobs'}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* HVAC Filters Section */}
        <div id="hvac-filters">
          <HvacFiltersSection
          hvacFilters={formData.hvacFilters}
          hvacFiltersLastChanged={formData.hvacFiltersLastChanged}
          onChange={(data) => {
            setFormData(prev => ({
              ...prev,
              hvacFilters: data.hvacFilters,
              hvacFiltersLastChanged: formatHvacLastChangedDate(data.hvacFiltersLastChanged),
            }));
          }}
        />
        </div>

        {/* Staging Photos Section */}
        <div id="staging-photos" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Staging Photos</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Photos
              </label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                {imagePreview ? (
                  <div className="space-y-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mx-auto max-h-48 rounded-lg"
                    />
                    <p className="text-sm text-gray-500">
                      Uploading...
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="text-sm text-gray-600">
                      <span className="text-blue-600 hover:text-blue-500">
                        Click to upload
                      </span>{' '}
                      or drag and drop
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>
              {isUploading && (
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Photo Gallery */}
            {formData.stagingPhotos.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Staging Photos</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {formData.stagingPhotos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={photo} 
                        alt={`Staging photo ${index + 1}`}
                        className="h-48 w-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove photo"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Guest Instructional Videos Section */}
        <div id="instructional-videos" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <GuestInstructionalVideosSection
            propertyId={id}
            instructionalVideos={formData.instructionalVideos}
            onChange={(next) => {
              setFormData(prev => ({
                ...prev,
                instructionalVideos: next.instructionalVideos
              }));
            }}
          />
        </div>
        
        {/* Form submit buttons */}
        <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBackToProperties}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {updateLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Archive/Delete Section */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-red-200 mt-8">
        <div className="px-4 py-5 sm:px-6 bg-red-50 border-b border-red-200">
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {relationsData?.propertyHasRelations ? (
              <div className="flex items-center justify-between p-4 border border-orange-200 rounded-lg bg-orange-50">
                <div>
                  <h4 className="text-sm font-medium text-orange-900">Archive Property</h4>
                  <p className="text-sm text-orange-700 mt-1">
                    This property has associated jobs, reservations, or work orders. 
                    You can archive it to hide it from active listings while preserving all data.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleArchiveProperty}
                  disabled={archiveLoading}
                  className="px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-white hover:bg-orange-50 disabled:opacity-50"
                >
                  {archiveLoading ? 'Archiving...' : 'Archive Property'}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                <div>
                  <h4 className="text-sm font-medium text-red-900">Delete Property</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This property has no associated data. You can permanently delete it from the system.
                    This action cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDeleteProperty}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Property'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md mx-auto p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Property
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to permanently delete "{formData.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3 justify-center">
                <button
                  type="button"
                  onClick={cancelAction}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmAction}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Dialog */}
      {showArchiveDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md mx-auto p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <svg
                  className="h-6 w-6 text-orange-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Archive Property
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to archive "{formData.name}"?
              </p>
              
              {futureJobsLoading ? (
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                  <span className="text-sm text-gray-500">Checking for future jobs...</span>
                </div>
              ) : futureJobsData?.getFutureJobsCount > 0 ? (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm text-orange-800">
                    <strong>Warning:</strong> This will cancel {futureJobsData.getFutureJobsCount} future job(s) scheduled for this property.
                    Past jobs will retain their status for reporting purposes.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    No future jobs found for this property. Only past jobs will be preserved for reporting.
                  </p>
                </div>
              )}
              
              <div className="flex space-x-3 justify-center">
                <button
                  type="button"
                  onClick={handleCancelArchive}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmArchive}
                  disabled={archiveLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
                >
                  {archiveLoading ? 'Archiving...' : 'Archive Property'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Future Walkthrough Jobs Confirmation Dialog */}
      {showDeleteWalkthroughDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md mx-auto p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Future Walkthrough Jobs
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to cancel all future walkthrough jobs for "{formData.name}"? 
                This will only affect jobs with PENDING or READY_FOR_INSPECTION status. 
                Completed and in-progress jobs will not be affected.
              </p>
              <div className="flex space-x-3 justify-center">
                <button
                  type="button"
                  onClick={() => setShowDeleteWalkthroughDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await deleteFutureWalkthroughJobs({
                        variables: { propertyId: id }
                      });
                      toast.success(`Successfully cancelled ${data.deleteFutureWalkthroughJobs} future walkthrough job(s)`);
                      setShowDeleteWalkthroughDialog(false);
                    } catch (error) {
                      toast.error('Failed to delete future walkthrough jobs: ' + error.message);
                    }
                  }}
                  disabled={deleteWalkthroughLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteWalkthroughLoading ? 'Deleting...' : 'Delete Jobs'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
